import React from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';

type Experience = {
  company: string;
  years: string;
  sentences: string[];
};

type HighlightsComponentProps = {
  location: string;
  spokenLanguages: string[];
  experiences: Experience[];
  keywords: string[];
  height?: string;
  maxWidth?: string;
  /** Minimum width of the card. Default: 400px */
  minWidth?: string;
};

const HighlightsComponent: React.FC<HighlightsComponentProps> = ({
  location,
  spokenLanguages,
  experiences,
  keywords,
  height = '500px',
  maxWidth = '1000px',
  minWidth = '400px', // NEW DEFAULT
}) => {
  const escaped = keywords.map((kw) =>
    kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );
  const regex = escaped.length
    ? new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi')
    : null;

  const highlightKeywords = (text: string) => {
    if (!regex) return text;
    const parts = text.split(regex);
    return parts.map((part, i) => {
      const match = keywords.some(
        (kw) => kw.toLowerCase() === part.toLowerCase()
      );
      return match ? (
        <span
          key={i}
          style={{
            backgroundColor: 'var(--primary)',
            color: '#fff',
            fontWeight: 600,
            padding: '1px 2px',
            borderRadius: '6px',
            display: 'inline-block',
          }}
        >
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      );
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          fontFamily: 'Montserrat',
          fontSize: '16px',
          fontWeight: 400,
          lineHeight: '24px',
          color: '#222',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
          maxWidth,
          minWidth, // <- APPLY MIN WIDTH
          width: '100%',
          height,
          overflow: 'hidden',
          margin: '0 20px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          className="typer-scroll-container"
          style={{
            flex: 1,
            width: '100%',
            overflowY: 'auto',
            padding: '20px',
            WebkitOverflowScrolling: 'touch',
            boxSizing: 'border-box',
          }}
        >
          {/* Location & Languages */}
          <div style={{ marginBottom: '16px', width: '100%' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px',
                width: '100%',
              }}
            >
              <LocationOnIcon style={{ color: 'var(--primary)', marginRight: '6px' }} />
              {location}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <LanguageIcon style={{ color: 'var(--primary)', marginRight: '6px' }} />
              {spokenLanguages.join(', ')}
            </div>
          </div>

          {/* Experience Blocks */}
          {experiences.length > 0 ? (
            experiences.map((exp, idx) => (
              <div key={idx} style={{ marginBottom: '24px', width: '100%' }}>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: '4px',
                    fontSize: '17px',
                  }}
                >
                  {exp.company}{' '}
                  <span style={{ fontWeight: 400, color: '#666' }}>
                    ({exp.years})
                  </span>
                </div>
                <ul style={{ paddingLeft: '20px', margin: 0 }}>
                  {exp.sentences.map((sentence, i) => (
                    <li key={i} style={{ marginBottom: '10px' }}>
                      {highlightKeywords(sentence)}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            <div style={{ color: '#999', textAlign: 'center', padding: '50px 0' }}>
              No experience added yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HighlightsComponent;
